import * as React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Stack, CircularProgress, Box, LinearProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQueryClient, useQuery } from "@tanstack/react-query";
// Contexts
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UIProvider } from "./contexts/UIContext";

import { ProtectedRoute } from "./components/ProtectedRoute/ProtectedRoute";

// --- Custom Hooks & Utils ---
import { getApiBaseUrl } from "./utils/apiUtils";
import { useAuthData } from "./hooks/useAuthData";
import { useSimulationData } from "./hooks/useSimulationData";
import { useAPI } from "./hooks/useAPI";
import { useBodyClasses } from "./hooks/useBodyClasses";

// --- Components ---
import AppTitle from "./components/AppTitle/AppTitle";
import Dashboard from "./components/complex/Dashboard";
import { TopLoader } from "./components/TopLoader/TopLoader";
import { CookieConsentInit } from "./components/CookieConsent/CookieConsentInit";

// --- Pages (Keep your existing imports) ---
import DashboardHome from "./components/DashboardHome/DashboardHome";
import Signup from "./pages/Signup";
import LoginPage from "./pages/Login/Login";
import LogoutPage from "./pages/Logout";
import ResetPassword from "./components/Account/ResetPassword/ResetPassword";
import { Info } from "./pages/Info";
import { LanderPage } from "./pages/Lander";
import { Error404 } from "./pages/Error404";
import DashboardTrainingPrograms from "./pages/dashboard/MyPrograms";
import DashboardProgram from "./pages/dashboard/Program";
import DashboardLeaderBoard from "./pages/dashboard/LeaderBoard";
import PastSimulations from "./components/PastSimulations/PastSimulations";
import DashboardTeam from "./pages/dashboard/Team";
import DashboardSettings from "./pages/dashboard/Settings";
import SkillsAcademy from "./components/SkillsAcademy/SkillsAcademy";
import SkillsAcademyListing from "./components/SkillsAcademy/SkillsAcademyListing";
import ProgramInfoPage from "./components/SkillsAcademy/ProgramInfoPage";
import CoachHome from "./pages/dashboard/CoachHome";
import ConversationInsights from "./components/ConversationInsights/ConversationInsights";

import TestAudioVisualizer from "./pages/TestAudio";
import { ExternalScenario } from "./pages/externalscenario/ExternalScenario";
import { ExternalScenarioDebrief } from "./pages/externalscenario/ExternalScenarioDebrief";
import { ExternalScenarioFeedback } from "./pages/externalscenario/ExternalScenarioFeedback";
import { ExternalOnDemandCoach } from "./pages/externalscenario/ExternalOnDemandCoach";
import ScenarioDetails from "./pages/dashboard/ScenarioDetails";
import MockCallScreen from "./pages/dashboard/MockCallScreen";
import LiveKitTest from "./pages/LiveKitTest";
import FeedbackCoach from "./components/FeedbackCoach/FeedbackCoach";
import Account from "./components/Account/Account";
import Profile from "./components/Account/Profile/Profile";
import SignUpInvitedUser from "./components/Account/SignUpInvitedUser/SignUpInvitedUser";
import TwoFA from "./components/Account/TwoFA/TwoFA";

// --- Helpers ---
import { fetchProgram } from "./components/helpers/FetchProgram";
import { fetchScenario } from "./components/helpers/FetchScenario";
import { fetchProgramFromScenarioId } from "./components/helpers/FetchProgramFromScenarioId";
import { fetchSkillsAcademyProgram } from "./components/helpers/FetchSkillsAcademyProgram";
import "./App.css";

export const BrandContext = React.createContext(null);

// --- MainContent lives outside App so it has a stable identity ---
function MainContent({
  brand,
  appLoading,
  setAppLoading,
  api_base_url,
  apiReady,
  partialAuthToken,
  setPartialAuthToken,
  queryClient,
}) {
  const { authToken, loggedInUser } = useAuth();
  const { callAPI } = useAPI();

  useBodyClasses();

  const currentPath = window.location.pathname.toLowerCase();
  const is2FA = currentPath.includes("2fa");
  const shouldFetch = !!authToken && !is2FA;

  const {
    trainingProgram,
    allTrainingPrograms,
    isLoading: authLoading,
  } = useAuthData(api_base_url, apiReady, authToken);

  const {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  } = useSimulationData(api_base_url, authToken, apiReady);

  const { data: featureFlags = {}, isLoading: isFlagsLoading } = useQuery({
    queryKey: ["feature_flags", authToken],
    queryFn: () => callAPI(`feature_flags`, { token: authToken }),
    staleTime: 5 * 60 * 1000,
    enabled: shouldFetch,
  });

  const contextValue = React.useMemo(
    () => ({
      brand,
      trainingProgram,
      allTrainingPrograms,
      appLoading,
      setAppLoading,
      simulations: simulationsData,
      refreshSimulations,
      fetchRecentSimulationsForScenario,
      partialAuthToken,
      setPartialAuthToken,
      featureFlags,
      isFlagsLoading,
    }),
    [
      trainingProgram,
      allTrainingPrograms,
      simulationsData,
      featureFlags,
      isFlagsLoading,
    ],
  );

  const router = React.useMemo(() => {
    if (appLoading) return null;

    return createBrowserRouter([
      // ── PUBLIC routes (unchanged) ──────────────────────────────────────────────
      {
        path: "/login",
        element: (
          <>
            <AppTitle title="Login" />
            <LoginPage />
          </>
        ),
      },
      { path: "/signup", element: <Signup /> },
      { path: "/signup/:signupFlowName", element: <Signup /> },
      {
        path: "/reset_password",
        element: (
          <>
            <AppTitle title="Reset Password" />
            <ResetPassword />
          </>
        ),
      },
      { path: "/signup/invite/:token", element: <SignUpInvitedUser /> },
      { path: "/2fa_init/:token", element: <TwoFA isSetup={true} /> },
      { path: "/2fa_verify/:token", element: <TwoFA isSetup={false} /> },
      { path: "/2fa_verify", element: <TwoFA isSetup={false} /> },
      { path: "/logout", element: <LogoutPage /> },
      {
        path: "/info/:pagename",
        element: <Info />,
        handle: { crumb: ({ params }) => params.pagename },
      },
      { path: "/lander/:pagename", element: <LanderPage /> },
      { path: "/testaudio", element: <TestAudioVisualizer /> },
      { path: "/404", element: <Error404 /> },
      {
        path: "version",
        element: (
          <div>Commit id: {import.meta.env.VITE_GIT_COMMIT ?? "N/A"}</div>
        ),
      },
      // External scenario routes are unauthenticated (token comes via URL param)
      {
        path: "/externalscenario",
        children: [
          {
            path: ":scenario_id",
            element: <ExternalScenario />,
            handle: { crumb: () => "External Scenario" },
          },
        ],
      },
      {
        path: "/externalscenario_debrief/:simulation_id",
        element: <ExternalScenarioDebrief />,
        handle: { crumb: () => "Debrief" },
      },
      {
        path: "/externalscenario_feedback/:simulation_id",
        element: <ExternalScenarioFeedback />,
        handle: { crumb: () => "Feedback" },
      },
      {
        path: "/external_ondemandcoach/:simulation_id",
        element: (
          <>
            <ExternalOnDemandCoach />
            <AppTitle title="On Demand Coach" />
          </>
        ),
        handle: { crumb: () => "External On Demand Coach" },
      },

      // ── PROTECTED routes (all wrapped by a single layout guard) ───────────────
      {
        element: <ProtectedRoute />, // ← the guard; renders <Outlet> when authed
        children: [
          // Root → Dashboard
          {
            path: "/",
            element: <Dashboard />,
            children: [{ index: true, element: <DashboardHome /> }],
          },

          // /user/...
          {
            path: "/user/",
            element: <Account />,
            handle: { crumb: () => "Account" },
            children: [
              {
                path: "settings",
                index: true,
                element: (
                  <>
                    <AppTitle title="Profile" />
                    <Profile />
                  </>
                ),
                handle: { crumb: () => "Profile" },
              },
              {
                path: "admin",
                element: null,
                handle: { crumb: () => "Team Management" },
              },
            ],
          },

          { path: "/livekit/:scenario_id", element: <LiveKitTest /> },

          // /dashboard/...
          {
            path: "/dashboard",
            element: <Dashboard />,
            handle: { crumb: () => "Dashboard" },
            children: [
              { index: true, element: <DashboardHome /> },
              {
                path: "home",
                element: <DashboardHome />,
                handle: { crumb: () => "Home" },
              },
              {
                path: "on_demand_coach",
                element: (
                  <>
                    <AppTitle title="On Demand Coaching" />
                    <CoachHome />
                  </>
                ),
                handle: { crumb: () => "On Demand Coaching" },
              },
              {
                path: "simulations",
                element: (
                  <>
                    <AppTitle title="Performance Spotlight" />
                    <PastSimulations />
                  </>
                ),
                handle: { crumb: () => "Performance Spotlight" },
              },
              {
                path: "trainingprograms",
                element: <DashboardTrainingPrograms />,
                handle: { crumb: () => "Training Programs" },
              },
              {
                path: "conversation-insights",
                element: (
                  <>
                    <AppTitle title="Conversation Insights" />
                    <ConversationInsights />
                  </>
                ),
                handle: { crumb: () => "Conversation Insights" },
              },
              {
                path: "team",
                element: (
                  <>
                    <AppTitle title="My Team" />
                    <DashboardTeam />
                  </>
                ),
                handle: { crumb: () => "My Team" },
              },
              {
                path: "leaderboard",
                element: <DashboardLeaderBoard />,
                handle: { crumb: () => "Leaderboard" },
              },
              {
                path: "skills-academy",
                element: <SkillsAcademy />,
                handle: { crumb: () => "Skills Academy" },
                children: [
                  {
                    index: true,
                    element: (
                      <>
                        <AppTitle title="Skills Academy" />
                        <SkillsAcademyListing />
                      </>
                    ),
                  },
                  {
                    path: ":training_program_id",
                    element: <ProgramInfoPage />,
                    loader: async ({ params }) => {
                      const program = await queryClient.ensureQueryData({
                        queryKey: [
                          "skills_academy",
                          "program",
                          params.training_program_id,
                        ],
                        queryFn: () =>
                          fetchSkillsAcademyProgram(
                            api_base_url,
                            authToken,
                            params.training_program_id,
                          ),
                        staleTime: 5 * 60 * 1000,
                      });
                      return {
                        programName: program?.name ?? "Program",
                      };
                    },
                    handle: {
                      crumb: (match, data) => data.programName,
                    },
                  },
                ],
              },
              {
                path: "settings",
                element: <DashboardSettings />,
                handle: { crumb: () => "Settings" },
              },
              {
                path: "mock-call/:scenario_id",
                element: <MockCallScreen />,
                handle: { crumb: () => "Simulation" },
              },
              {
                path: "trainingprogram/:trainingprogram_id",
                element: (
                  <>
                    <AppTitle title="My Programs" />
                    <DashboardProgram />
                  </>
                ),
                loader: async ({ params }) => {
                  const queryKey = [
                    "trainingProgramBreadcrumb",
                    params.trainingprogram_id,
                  ];
                  let program = queryClient
                    .getQueryData(["trainingPrograms"])
                    ?.find(
                      (p) => String(p.id) === String(params.trainingprogram_id),
                    );
                  if (!program) {
                    program = await queryClient.fetchQuery({
                      queryKey,
                      queryFn: () =>
                        fetchProgram(
                          api_base_url,
                          authToken,
                          params.trainingprogram_id,
                        ),
                      staleTime: 5 * 60 * 1000,
                    });
                  }
                  return {
                    programName: program.name ?? "Program",
                    programUrl: `/dashboard/trainingprogram/${program?.id}`,
                  };
                },
                handle: {
                  crumb: (match, data) => [
                    { label: "My Programs", to: "/dashboard/trainingprograms" },
                    { label: data.programName, to: data.programUrl },
                  ],
                },
              },
              {
                path: "simulate/:scenario_id",
                element: <ScenarioDetails />,
                loader: async ({ params }) => {
                  const { scenario_id } = params;
                  const [scenario, program] = await Promise.all([
                    queryClient.ensureQueryData({
                      queryKey: ["scenario", String(scenario_id)],
                      queryFn: () =>
                        fetchScenario(api_base_url, authToken, scenario_id),
                      staleTime: 1000 * 60 * 5,
                    }),
                    queryClient.ensureQueryData({
                      queryKey: ["program_by_scenario", String(scenario_id)],
                      queryFn: () =>
                        fetchProgramFromScenarioId(
                          api_base_url,
                          authToken,
                          scenario_id,
                        ),
                      staleTime: 1000 * 60 * 5,
                    }),
                  ]);
                  return {
                    programName: program.name ?? "Program",
                    programUrl: `/dashboard/trainingprogram/${program.id}`,
                    scenarioName: scenario.name ?? "Scenario",
                    scenarioUrl: `/dashboard/simulate/${scenario?.id}`,
                  };
                },
                handle: {
                  crumb: (match, data) => [
                    {
                      label: "Training Programs",
                      to: "/dashboard/trainingprograms",
                    },
                    { label: data.programName, to: data.programUrl },
                    { label: data.scenarioName, to: data.scenarioUrl },
                  ],
                },
              },
              {
                path: "feedback/:simulation_id",
                element: <FeedbackCoach />,
                handle: {
                  crumb: () => [
                    {
                      label: "Performance Spotlight",
                      to: "/dashboard/simulations",
                    },
                    { label: "Feedback", to: "/" },
                  ],
                },
              },
            ],
          },
        ],
      },
    ]);
  }, [appLoading, api_base_url, authToken]);

  return (
    <BrandContext.Provider value={contextValue}>
      <RouterProvider router={router} />
    </BrandContext.Provider>
  );
}

function App() {
  const [brand, setBrand] = React.useState(null);
  const [appLoading, setAppLoading] = React.useState(true);
  const [partialAuthToken, setPartialAuthToken] = React.useState(null);

  const api_base_url = React.useMemo(() => getApiBaseUrl(), []);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setBrand({ api_base_url, name: "GuruNow" });
    document.title = "GuruNow";
    setAppLoading(false);
  }, [api_base_url]);

  const [apiReady, setApiReady] = React.useState(
    () => document.visibilityState === "visible",
  );

  React.useEffect(() => {
    function handleVisibilityChange() {
      setApiReady(document.visibilityState === "visible");
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  if (appLoading) {
    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 1, height: "100vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <React.StrictMode>
      <AuthProvider>
        <UIProvider>
          <CookieConsentInit />
          <TopLoader />
          <MainContent
            brand={brand}
            appLoading={appLoading}
            setAppLoading={setAppLoading}
            api_base_url={api_base_url}
            apiReady={apiReady}
            partialAuthToken={partialAuthToken}
            setPartialAuthToken={setPartialAuthToken}
            queryClient={queryClient}
          />
        </UIProvider>
      </AuthProvider>
    </React.StrictMode>
  );
}

export default App;
